import React, { FC, useEffect, useState } from "react";
import { Autocomplete, TextField, Chip, CircularProgress, MenuItem } from "@mui/material";
import { Check as CheckIcon } from "@mui/icons-material";
import { IDropDownListItem } from "./DropDownField";
import FloatingInputLabel from "../components/FloatingInputLabel";

interface MultiSelectFieldProps {
    label: string;
    selectedItems: IDropDownListItem[];
    onRequestData: () => Promise<IDropDownListItem[]>;
    onChange: (value: IDropDownListItem[]) => void;
    maxWidth?: number | string;
    readOnly?: boolean;
    testId?: string;
    placeholder?: string;
    limitTags?: number;
}

type InteractionState = 'inactive' | 'focused' | 'open';

const getInputStyles = (state: InteractionState, limitTags?: number) => {
    const isCompact = state === 'inactive' && limitTags;
    const isExpanded = state === 'focused' || state === 'open';

    return {
        borderRadius: 5,
        padding: "3px 8px",
        width: "100%",
        minHeight: "40px",
        maxHeight: isCompact ? "40px" : "none",
        height: isCompact ? "40px" : "auto",
        fontSize: "inherit",
        fontFamily: "inherit",
        display: "flex",
        flexWrap: isExpanded ? "wrap" : "nowrap",
        alignItems: "center",
        alignContent: "flex-start",
        overflow: isCompact ? "hidden" : "visible",
        gap: "4px"
    } as const;
};

const getChipStyles = () => ({
    margin: "2px 0",
    height: "24px",
    fontSize: "12px",
    maxWidth: "160px",
    overflow: "hidden"
} as const);

const truncateText = (text: string, isMultiple: boolean): string => {
    const maxLength = isMultiple ? 12 : 20;
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

const MultiSelectField: FC<MultiSelectFieldProps> = ({
    label,
    selectedItems,
    onRequestData,
    onChange,
    maxWidth = 225,
    readOnly = false,
    testId,
    placeholder,
    limitTags
}) => {
    const [options, setOptions] = useState<IDropDownListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [interactionState, setInteractionState] = useState<InteractionState>("inactive");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (onRequestData) {
                setLoading(true);
                try {
                    const fetchedOptions = await onRequestData();
                    if (!cancelled) {
                        setOptions(fetchedOptions || []);
                    }
                } catch (ex) {
                    if (!cancelled) {
                        setOptions([]);
                    }
                }
                setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [onRequestData]);

    const handleChange = (_event: React.SyntheticEvent, newValue: IDropDownListItem[]) => {
        onChange(newValue);
    };

    const handleOpen = () => setInteractionState("open");
    const handleClose = () => setInteractionState("inactive");
    const handleFocus = () => setInteractionState("focused");
    const handleBlur = () => setInteractionState("inactive");

    const getOptionLabel = (option: IDropDownListItem) => option.name || "";
    const isOptionEqualToValue = (option: IDropDownListItem, value: IDropDownListItem) =>
        option.value === value.value;

    // TODO: DD - Add a loading skeleton instead of just a circular progress
    if (loading) {
        return <CircularProgress size={24} />;
    }

    const inputStyles = getInputStyles(interactionState, limitTags);
    const isMultipleSelections = (selectedItems?.length || 0) > 1;

    return (
        <FloatingInputLabel label={label} maxWidth={maxWidth}>
            <Autocomplete
                data-testid={testId}
                multiple
                disabled={readOnly}
                limitTags={limitTags}
                options={options}
                value={selectedItems || []}
                onChange={handleChange}
                onOpen={handleOpen}
                onClose={handleClose}
                onFocus={handleFocus}
                onBlur={handleBlur}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={isOptionEqualToValue}
                disableCloseOnSelect
                openOnFocus
                sx={{
                    width: "100%",
                    maxWidth: maxWidth,
                    // TODO: DD - remove these once we remove the mui5 button hardcoded 48x48px
                    '& .MuiAutocomplete-popupIndicator': {
                        width: 'auto',
                        height: 'auto',
                        padding: '0',
                    },
                    '& .MuiAutocomplete-clearIndicator': {
                        width: 'auto',
                        height: 'auto',
                        padding: '4px',
                    }
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        placeholder={placeholder}
                        InputProps={{
                            ...params.InputProps,
                            style: inputStyles
                        }}
                        InputLabelProps={{
                            shrink: false,
                            style: { display: 'none' }
                        }}
                        style={{
                            width: "100%",
                            maxWidth: maxWidth,
                        }}
                    />
                )}
                renderOption={(props, option, { selected }) => (
                    <MenuItem
                        {...props}
                        key={option.value}
                        value={option.value}
                        style={{ justifyContent: "space-between" }}
                    >
                        {option.name}
                        {/* TODO: DD - Add the colour from the palette after mui5 upgrade */}
                        {selected ? <CheckIcon color="info" /> : null}
                    </MenuItem>
                )}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                        const truncatedName = truncateText(option.name, isMultipleSelections);
                        const chipStyles = getChipStyles();

                        return (
                            <Chip
                                {...getTagProps({ index })}
                                key={option.value}
                                label={truncatedName}
                                title={option.name}
                                size="small"
                                variant="outlined"
                                onDelete={readOnly ? undefined : getTagProps({ index }).onDelete}
                                style={chipStyles}
                            />
                        );
                    })
                }
            />
        </FloatingInputLabel>
    );
};

export default MultiSelectField; 